import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const checkPartnerStatus = createAsyncThunk('partner/checkStatus', async (userId) => {
  const r = await api.get(`/partner/status/${userId}`); return r.data;
});
export const getPartnerProfile = createAsyncThunk('partner/getProfile', async (userId, { rejectWithValue }) => {
  try {
    const r = await api.get(`/partner/profile/${userId}`);
    return r.data;
  }
  catch (e) {
    return rejectWithValue(e.response?.data || { exists: false });
  }
}, {
  // Use Redux Toolkit condition to prevent duplicate fetching if already loading
  condition: (userId, { getState }) => {
    const { partner } = getState();
    if (partner.loading && partner.profileChecked === false) {
      return false;
    }
  }
});
export const registerPartner = createAsyncThunk('partner/register', async (data) => {
  const r = await api.post(`/partner/register`, data); return r.data;
});
export const updateBusinessDetails = createAsyncThunk('partner/updateBusiness', async ({ userId, businessDetails }) => {
  const r = await api.patch(`/partner/business/${userId}`, { businessDetails }); return r.data;
});
export const uploadKYCDocument = createAsyncThunk('partner/uploadKYC', async ({ userId, document }) => {
  const r = await api.post(`/partner/kyc/upload/${userId}`, document); return r.data;
});
export const submitKYC = createAsyncThunk('partner/submitKYC', async (userId) => {
  const r = await api.post(`/partner/kyc/submit/${userId}`); return r.data;
});
export const updateBankDetails = createAsyncThunk('partner/updateBank', async ({ userId, bankDetails }) => {
  const r = await api.patch(`/partner/bank/${userId}`, { bankDetails }); return r.data;
});
export const selectPlan = createAsyncThunk('partner/selectPlan', async ({ userId, plan }, { rejectWithValue }) => {
  try {
    const r = await api.post(`/partner/plan/${userId}`, { plan }); 
    return r.data;
  } catch (err) {
    console.error("🚨 Terminal Hub Activation Failure:", err.response?.data || err.message);
    return rejectWithValue(err.response?.data || err.message);
  }
});
export const getDashboardSummary = createAsyncThunk('partner/dashboard', async (userId) => {
  const r = await api.get(`/partner/dashboard/${userId}`); return r.data;
});
export const addNewTheater = createAsyncThunk('partner/addTheater', async ({ userId, theaterData }) => {
  const r = await api.post(`/partner/add-theater/${userId}`, { theaterData }); return r.data;
});

// Admin
export const getAllPartners = createAsyncThunk('partner/getAll', async (params) => {
  const r = await api.get(`/partner/admin/all`, { params }); return r.data;
});
export const getKYCQueue = createAsyncThunk('partner/kycQueue', async (params) => {
  const r = await api.get(`/partner/admin/kyc-queue`, { params }); return r.data;
});
export const getPartnerNotifications = createAsyncThunk('partner/notifications', async () => {
  const r = await api.get(`/partner/admin/notifications`); return r.data;
});
export const approveKYC = createAsyncThunk('partner/approveKYC', async ({ partnerId, reviewedBy }) => {
  const r = await api.post(`/partner/admin/kyc/${partnerId}/approve`, { reviewedBy }); return r.data;
});
export const rejectKYC = createAsyncThunk('partner/rejectKYC', async ({ partnerId, reviewedBy, reason }) => {
  const r = await api.post(`/partner/admin/kyc/${partnerId}/reject`, { reviewedBy, reason }); return r.data;
});
export const suspendPartner = createAsyncThunk('partner/suspend', async ({ partnerId, reason }) => {
  const r = await api.post(`/partner/admin/${partnerId}/suspend`, { reason }); return r.data;
});
export const reactivatePartner = createAsyncThunk('partner/reactivate', async ({ partnerId }) => {
  const r = await api.post(`/partner/admin/${partnerId}/reactivate`); return r.data;
});

const partnerSlice = createSlice({
  name: 'partner',
  initialState: {
    partner: null, isPartner: false, onboardingStep: 0, onboardingCompleted: false,
    kycStatus: null, currentPlan: null, dashboardStats: null,
    allPartners: [], kycQueue: [], notifications: null,
    pagination: null, loading: false, error: null, profileChecked: false,
  },
  reducers: {
    clearPartnerState: (state) => { state.partner = null; state.isPartner = false; state.error = null; state.profileChecked = false; },
    setPartner: (state, action) => { state.partner = action.payload; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkPartnerStatus.fulfilled, (state, action) => {
        if (action.payload) {
          Object.assign(state, action.payload);
          if (action.payload.partner) state.partner = action.payload.partner;
        }
      })
      .addCase(getPartnerProfile.pending, (state) => { state.loading = true; })
      .addCase(getPartnerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileChecked = true;

        const data = action.payload;
        if (!data) return;

        state.partner = data.partner || null;
        state.isPartner = !!data.exists;

        if (data.partner) {
          state.onboardingStep = data.partner.onboardingStep || 0;
          state.onboardingCompleted = !!data.partner.onboardingCompleted;
          state.kycStatus = data.partner.kycStatus || 'PENDING_SUBMISSION';
          state.currentPlan = data.partner.currentPlan || 'Starter';
        }
      })
      .addCase(getPartnerProfile.rejected, (state) => {
        state.loading = false; state.profileChecked = true; state.isPartner = false; state.partner = null;
      })
      .addCase(registerPartner.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.partner = action.payload.partner;
        state.isPartner = true;
        state.onboardingStep = 2;
      })
      .addCase(updateBusinessDetails.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.partner = action.payload.partner;
        state.onboardingStep = Math.max(state.onboardingStep, 3);
      })
      .addCase(uploadKYCDocument.fulfilled, (state, action) => {
        if (action.payload?.partner) state.partner = action.payload.partner;
      })
      .addCase(submitKYC.fulfilled, (state, action) => {
        if (action.payload?.partner) {
          state.partner = action.payload.partner;
          state.onboardingStep = Math.max(state.onboardingStep, 4);
          state.kycStatus = 'UNDER_REVIEW';
        }
      })
      .addCase(updateBankDetails.fulfilled, (state, action) => {
        if (action.payload?.partner) {
          state.partner = action.payload.partner;
          state.onboardingStep = Math.max(state.onboardingStep, 5);
        }
      })
      .addCase(selectPlan.fulfilled, (state, action) => {
        if (action.payload?.partner) {
          state.partner = action.payload.partner;
          state.currentPlan = action.payload.partner.currentPlan;
          state.onboardingCompleted = true;
        }
      })
      .addCase(getDashboardSummary.fulfilled, (state, action) => {
        if (action.payload) {
          state.partner = action.payload.partner;
          state.dashboardStats = action.payload.stats;
        }
      })
      .addCase(addNewTheater.fulfilled, (state, action) => {
        if (action.payload?.partner) state.partner = action.payload.partner;
      })
      .addCase(getAllPartners.pending, (state) => { state.loading = true; })
      .addCase(getAllPartners.fulfilled, (state, action) => {
        state.loading = false; state.allPartners = action.payload.partners; state.pagination = action.payload.pagination;
      })
      .addCase(getKYCQueue.fulfilled, (state, action) => { state.kycQueue = action.payload.partners; })
      .addCase(getPartnerNotifications.fulfilled, (state, action) => { state.notifications = action.payload; })
      .addCase(approveKYC.fulfilled, (state, action) => {
        state.kycQueue = state.kycQueue.filter(p => p._id !== action.payload.partner._id);
      })
      .addCase(rejectKYC.fulfilled, (state, action) => {
        state.kycQueue = state.kycQueue.filter(p => p._id !== action.payload.partner._id);
        const idx = state.allPartners.findIndex(p => p._id === action.payload.partner._id);
        if (idx !== -1) state.allPartners[idx] = action.payload.partner;
      })
      .addCase(suspendPartner.fulfilled, (state, action) => {
        const idx = state.allPartners.findIndex(p => p._id === action.payload.partner._id);
        if (idx !== -1) state.allPartners[idx] = action.payload.partner;
      })
      .addCase(reactivatePartner.fulfilled, (state, action) => {
        const idx = state.allPartners.findIndex(p => p._id === action.payload.partner._id);
        if (idx !== -1) state.allPartners[idx] = action.payload.partner;
      });
  }
});

export const { clearPartnerState, setPartner } = partnerSlice.actions;
export default partnerSlice.reducer;
